import {
  Background,
  type Connection,
  Controls,
  type Edge,
  type Node,
  ReactFlow,
  type ReactFlowInstance,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useLoaderData, useNavigate } from "@tanstack/react-router";
import { LayoutGrid, Maximize2 } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { ManagerEdge } from "./manager-edge";
import {
  buildNodesAndEdges,
  detectCycle,
  layoutTree,
  type OrgOwner,
  OWNER_DRAG_MIME,
  type OwnerNodeData,
} from "./org-chart-utils";
import { OwnerNode } from "./owner-node";
import { OwnerPanel } from "./owner-panel";

const nodeTypes = { ownerNode: OwnerNode };
const edgeTypes = { managerEdge: ManagerEdge };

type OrgNode = Node<OwnerNodeData>;

export function OrgChartPage({
  readOnly = false,
  isAdmin = false,
}: {
  readOnly?: boolean;
  isAdmin?: boolean;
}) {
  // biome-ignore lint/suspicious/noExplicitAny: route type not yet in TanStack Router's generated types
  const { owners: initialOwners } = useLoaderData({
    from: "/_authenticatedFullBleed/org-chart" as any,
  }) as {
    owners: OrgOwner[];
  };

  const navigate = useNavigate();
  const [owners, setOwners] = useState<OrgOwner[]>(initialOwners);
  const [rootIds, setRootIds] = useState<Set<string>>(new Set());
  const ownerMap = useMemo(() => new Map(owners.map((o) => [o.id, o])), [owners]);

  const [nodes, setNodes, onNodesChange] = useNodesState<OrgNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [unassigned, setUnassigned] = useState<OrgOwner[]>([]);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const reactFlowInstance = useRef<ReactFlowInstance<OrgNode, Edge> | null>(null);

  const handleViewProfile = useCallback(
    (ownerId: string) => {
      navigate({
        to: "/owner-detail/$ownerId",
        params: { ownerId },
        search: { from: "org-chart" },
      });
    },
    [navigate],
  );

  const handleViewDashboard = useCallback(
    (hubspotOwnerId: string) => {
      navigate({
        to: "/owner-metrics/$ownerId",
        params: { ownerId: hubspotOwnerId },
        search: { from: "org-chart" },
      });
    },
    [navigate],
  );

  const handleDeleteEdge = useCallback((targetId: string) => {
    if (!targetId) return;
    fetch("/api/org-chart/relationship", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ownerId: targetId, managerId: null }),
    }).catch((err) => console.error("Failed to delete edge:", err));

    setOwners((prev) => prev.map((o) => (o.id === targetId ? { ...o, managerId: null } : o)));
  }, []);

  const handleRemoveNode = useCallback((ownerId: string) => {
    // Clear this node's manager on server
    fetch("/api/org-chart/relationship", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ownerId, managerId: null }),
    }).catch((err) => console.error("Failed to remove node:", err));

    setOwners((prev) => {
      // Also detach children on server
      for (const o of prev) {
        if (o.managerId === ownerId) {
          fetch("/api/org-chart/relationship", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ownerId: o.id, managerId: null }),
          }).catch((err) => console.error("Failed to detach child:", err));
        }
      }
      return prev
        .map((o) => (o.id === ownerId ? { ...o, managerId: null } : o))
        .map((o) => (o.managerId === ownerId ? { ...o, managerId: null } : o));
    });
    setRootIds((prev) => {
      const next = new Set(prev);
      next.delete(ownerId);
      return next;
    });
  }, []);

  // Build chart whenever owners/rootIds/callbacks change
  const rebuildChart = useCallback(
    (currentOwners: OrgOwner[], currentRootIds: Set<string>) => {
      const result = buildNodesAndEdges(currentOwners, currentRootIds);
      // Inject callbacks into node data
      const laidOutNodes = layoutTree(
        result.nodes.map((n) => ({
          ...n,
          data: {
            ...n.data,
            isAdmin,
            onRemove: readOnly ? undefined : handleRemoveNode,
            onViewProfile: handleViewProfile,
            onViewDashboard: handleViewDashboard,
          },
        })),
        result.edges,
      );
      // Switch edge type to custom and inject onDelete
      const customEdges = result.edges.map((e) => ({
        ...e,
        type: "managerEdge" as const,
        data: { onDelete: readOnly ? undefined : handleDeleteEdge },
      }));
      setNodes(laidOutNodes);
      setEdges(customEdges);
      setUnassigned(result.unassigned);
    },
    [
      isAdmin,
      readOnly,
      setNodes,
      setEdges,
      handleDeleteEdge,
      handleRemoveNode,
      handleViewProfile,
      handleViewDashboard,
    ],
  );

  // Rebuild when owners or rootIds change
  // biome-ignore lint/correctness/useExhaustiveDependencies: rebuildChart is intentionally excluded to avoid infinite loop
  useMemo(() => {
    rebuildChart(owners, rootIds);
  }, [owners, rootIds, handleDeleteEdge, handleRemoveNode, handleViewProfile, handleViewDashboard]);

  const handleOwnerCreated = useCallback(async () => {
    try {
      const res = await fetch("/api/owners?pageSize=1000");
      if (!res.ok) return;
      const data = await res.json();
      setOwners(
        data.owners.map((o: OrgOwner) => ({
          id: o.id,
          firstName: o.firstName,
          lastName: o.lastName,
          email: o.email,
          role: o.role,
          hubspotOwnerId: o.hubspotOwnerId,
          managerId: o.managerId,
        })),
      );
    } catch (err) {
      console.error("Failed to refresh owners:", err);
    }
  }, []);

  const updateRelationship = useCallback(
    async (ownerId: string, managerId: string | null) => {
      const res = await fetch("/api/org-chart/relationship", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ownerId, managerId }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed to update" }));
        console.error("Relationship update failed:", err.error);
        return false;
      }

      const updated = owners.map((o) => (o.id === ownerId ? { ...o, managerId } : o));
      // If assigning a manager, remove from explicit roots
      if (managerId && rootIds.has(ownerId)) {
        const newRootIds = new Set(rootIds);
        newRootIds.delete(ownerId);
        setRootIds(newRootIds);
      }
      setOwners(updated);
      return true;
    },
    [owners, rootIds],
  );

  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: OrgNode) => {
      if (!reactFlowInstance.current) return;

      const intersecting = reactFlowInstance.current.getIntersectingNodes(node);
      const target = intersecting.find((n) => n.id !== node.id);
      if (!target) return;

      if (detectCycle(target.id, node.id, ownerMap)) {
        console.error("Cannot create cycle");
        return;
      }

      updateRelationship(node.id, target.id);
    },
    [ownerMap, updateRelationship],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      if (detectCycle(connection.source, connection.target, ownerMap)) {
        console.error("Cannot create cycle");
        return;
      }
      updateRelationship(connection.target, connection.source);
    },
    [ownerMap, updateRelationship],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const ownerId = event.dataTransfer.getData(OWNER_DRAG_MIME);
      if (!ownerId || !reactFlowInstance.current) return;

      const position = reactFlowInstance.current.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      let nearestNodeId: string | null = null;
      let minDist = Number.POSITIVE_INFINITY;

      for (const node of nodes) {
        const dx = node.position.x + 140 - position.x;
        const dy = node.position.y + 65 - position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < minDist) {
          minDist = dist;
          nearestNodeId = node.id;
        }
      }

      if (nearestNodeId && minDist < 300) {
        updateRelationship(ownerId, nearestNodeId);
      } else {
        const newRootIds = new Set(rootIds);
        newRootIds.add(ownerId);
        setRootIds(newRootIds);
        const updated = owners.map((o) => (o.id === ownerId ? { ...o, managerId: null } : o));
        setOwners(updated);
      }
    },
    [nodes, owners, rootIds, updateRelationship],
  );

  const handleFitView = useCallback(() => {
    reactFlowInstance.current?.fitView({ padding: 0.2 });
  }, []);

  const handleAutoLayout = useCallback(() => {
    setNodes((currentNodes) => layoutTree([...currentNodes], [...edges]));
    setTimeout(() => reactFlowInstance.current?.fitView({ padding: 0.2 }), 50);
  }, [edges, setNodes]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 lg:px-6 pt-4 lg:pt-6 pb-2">
        <PageHeader
          title={isAdmin ? "Organization Chart" : "My Team"}
          actions={
            <>
              <Button variant="outline" size="sm" onClick={handleAutoLayout}>
                <LayoutGrid className="h-4 w-4 mr-2" />
                Auto Layout
              </Button>
              <Button variant="outline" size="sm" onClick={handleFitView}>
                <Maximize2 className="h-4 w-4 mr-2" />
                Fit View
              </Button>
            </>
          }
        />
      </div>

      <div className="flex-1 flex min-h-0">
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={readOnly ? undefined : onEdgesChange}
            onNodeDragStop={readOnly ? undefined : onNodeDragStop}
            onConnect={readOnly ? undefined : onConnect}
            onDragOver={readOnly ? undefined : onDragOver}
            onDrop={readOnly ? undefined : onDrop}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            edgesReconnectable={!readOnly}
            onInit={(instance) => {
              reactFlowInstance.current = instance;
            }}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            proOptions={{ hideAttribution: true }}
          >
            <Controls className="[&_button]:!bg-[#0E3752] [&_button]:!text-white [&_button]:!border-[#0E3752] [&_button:hover]:!bg-[#1a4d6e]" />
            <Background color="#0E3752" gap={20} size={1} style={{ opacity: 0.15 }} />
          </ReactFlow>

          {nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <p className="text-muted-foreground text-lg">
                  {readOnly ? "No team members to display" : "No org chart data yet"}
                </p>
                {!readOnly && (
                  <p className="text-muted-foreground text-sm mt-1">
                    Drag owners from the panel to build the chart
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {!readOnly && (
          <OwnerPanel
            owners={unassigned}
            collapsed={panelCollapsed}
            onToggle={() => setPanelCollapsed(!panelCollapsed)}
            onOwnerCreated={handleOwnerCreated}
          />
        )}
      </div>
    </div>
  );
}
